/**
 * Per-route SEO meta configuration.
 *
 * Single source of truth lives in seo-data.json.
 * This module re-exports it with TypeScript types for use in the SPA.
 */

import config, { PROD_SITE_URL } from './index'
import seoData from './seo-data.json'

export interface RouteSeoMeta {
    title: string
    description: string
    ogTitle?: string
    ogDescription?: string
    path: string
    ogImage?: string
    keywords?: string
    /** Short crumb label for the per-route BreadcrumbList (e.g. "Ask Sovi").
     *  Present only on functional product pages we want surfaced as the site
     *  directory structure in search; absent => no breadcrumb is injected. */
    breadcrumb?: string
    locales?: Record<string, Partial<Omit<RouteSeoMeta, 'path' | 'locales'>>>
}

export const SITE_NAME: string = seoData.siteName
export const SITE_URL: string = config.siteUrl
export const DEFAULT_OG_IMAGE: string = resolveOgImage(seoData.defaultOgImage) || `${config.siteUrl}/og-image.png`

function resolveOgImage(ogImage: string | undefined): string | undefined {
    if (!ogImage) return undefined
    if (ogImage.startsWith('http')) return ogImage
    return `${config.siteUrl}${ogImage}`
}

const SITE: string = (SITE_URL || PROD_SITE_URL).replace(/\/+$/, '')

/** Default meta-description length (matches the edge worker's truncateForMeta). */
const META_DESC_MAX = 155

/**
 * Build a self-referential canonical URL for a path, mirroring the edge
 * function (`functions/[[path]].js`): home → `${SITE}/`, otherwise
 * `${SITE}${path}` with the trailing slash stripped and query/hash removed.
 */
export function canonicalFor(path?: string): string {
    const raw = path || (typeof location !== 'undefined' ? location.pathname : '/') || '/'
    const clean = raw.split('?')[0].split('#')[0]
    if (clean === '/' || clean === '') return `${SITE}/`
    return `${SITE}${clean.replace(/\/+$/, '')}`
}

/** Trim a description to a SERP-friendly length (mirrors the edge truncateForMeta). */
export function truncateForMeta(text?: string, max = META_DESC_MAX): string {
    if (!text) return ''
    const clean = String(text).replace(/\s+/g, ' ').trim()
    if (clean.length <= max) return clean
    const cut = clean.slice(0, max - 1)
    const lastSpace = cut.lastIndexOf(' ')
    const safe = lastSpace >= max - 20 ? cut.slice(0, lastSpace) : cut
    return safe.replace(/[\s.,;:!?\-–—]+$/u, '') + '…'
}

/** Point <link rel="canonical"> + og:url at an absolute URL (creates the link if absent). */
export function setCanonical(href: string): void {
    if (typeof document === 'undefined' || !href) return
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
    if (!link) {
        link = document.createElement('link')
        link.setAttribute('rel', 'canonical')
        document.head.appendChild(link)
    }
    link.setAttribute('href', href)
    const ogUrl = document.querySelector('meta[property="og:url"]')
    if (ogUrl) ogUrl.setAttribute('content', href)
}

/**
 * Inject/refresh a per-route BreadcrumbList (Home → current page) in <head> so
 * search engines can render the site directory path for functional product
 * pages (/chat, /apexam, /resources, …). The crumb item URL mirrors
 * canonicalFor(). Pass no `name` to remove any existing breadcrumb — home and
 * non-functional routes carry none, so navigating away clears it.
 *
 * For crawler user-agents the edge worker (functions/[[path]].js) already injects
 * a server-side BreadcrumbList into the initial HTML. To avoid emitting a
 * duplicate when such a crawler also renders JS, we skip if a non-self
 * BreadcrumbList is already present; real browsers get the SPA shell without one,
 * so the client injection still runs for actual users.
 */
const BREADCRUMB_ID = 'route-breadcrumb-jsonld'
function hasServerBreadcrumb(): boolean {
    const scripts = document.querySelectorAll('script[type="application/ld+json"]')
    for (const s of Array.from(scripts)) {
        if (s.id === BREADCRUMB_ID) continue
        if ((s.textContent || '').includes('"BreadcrumbList"')) return true
    }
    return false
}
export function setBreadcrumbJsonLd(name?: string, path?: string): void {
    if (typeof document === 'undefined') return
    let el = document.getElementById(BREADCRUMB_ID) as HTMLScriptElement | null
    if (!name) {
        if (el) el.remove()
        return
    }
    // A server-injected BreadcrumbList already covers this page (crawler path).
    if (hasServerBreadcrumb()) {
        if (el) el.remove()
        return
    }
    if (!el) {
        el = document.createElement('script')
        el.id = BREADCRUMB_ID
        el.type = 'application/ld+json'
        document.head.appendChild(el)
    }
    const data = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
            { '@type': 'ListItem', position: 2, name, item: canonicalFor(path) }
        ]
    }
    el.textContent = JSON.stringify(data)
}
/** SEO meta keyed by route name. */
export const routeSeoMap: Record<string, RouteSeoMeta> = Object.fromEntries(
    Object.entries(seoData.routes).map(([name, r]) => [
        name,
        {
            title: r.title,
            description: r.description,
            path: r.path,
            ogImage: resolveOgImage(r.ogImage),
            ogTitle: r.ogTitle,
            ogDescription: r.ogDescription,
            keywords: r.keywords,
            breadcrumb: (r as { breadcrumb?: string }).breadcrumb,
            locales: (r as { locales?: RouteSeoMeta['locales'] }).locales
        }
    ])
)

/** Default SEO meta — uses the home route config. */
export const defaultSeoMeta: RouteSeoMeta = routeSeoMap.home

export function resolveLocalizedSeoMeta(seo: RouteSeoMeta, lang?: string): RouteSeoMeta {
    const localized = lang && lang !== 'en' ? seo.locales?.[lang] : undefined
    return localized ? { ...seo, ...localized, path: seo.path, locales: seo.locales } : seo
}

export function updateDynamicSeo(seo: Partial<RouteSeoMeta>) {
    if (typeof document === 'undefined') return;

    if (seo.title) document.title = seo.title

    const desc = truncateForMeta(seo.description)
    const descMeta = document.querySelector('meta[name="description"]')
    if (descMeta && desc) descMeta.setAttribute('content', desc)

    const keywordsMeta = document.querySelector('meta[name="keywords"]')
    if (keywordsMeta) keywordsMeta.setAttribute('content', seo.keywords || '')

    const ogTitle = document.querySelector('meta[property="og:title"]')
    if (ogTitle && (seo.ogTitle || seo.title)) ogTitle.setAttribute('content', (seo.ogTitle || seo.title) as string)

    const ogDescMeta = document.querySelector('meta[property="og:description"]')
    if (ogDescMeta && (seo.ogDescription || desc)) ogDescMeta.setAttribute('content', (seo.ogDescription || desc) as string)

    const ogImage = document.querySelector('meta[property="og:image"]')
    if (ogImage && seo.ogImage) {
        let finalImage = seo.ogImage;
        if (!finalImage.startsWith('http') && !finalImage.startsWith('/')) {
             finalImage = `/${finalImage}`;
        }
        ogImage.setAttribute('content', finalImage)

        const ogImageWidth = document.querySelector('meta[property="og:image:width"]')
        if (ogImageWidth) ogImageWidth.setAttribute('content', "1200")

        const ogImageHeight = document.querySelector('meta[property="og:image:height"]')
        if (ogImageHeight) ogImageHeight.setAttribute('content', "630")
    }
    // Canonical + og:url are owned centrally by the router hook in main.ts
    // (`setCanonical(canonicalFor(to.path))` on every navigation), so we don't
    // re-set them here — callers pass no path, so it would only re-derive the
    // same current-path value.
}
